import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  IconButton,
  Button,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import dayjs from "dayjs";

const getStatusProps = (statusRaw) => {
  const s = String(statusRaw || "").trim().toLowerCase();

  switch (s) {
    case "rejected":
      return { color: "error", label: statusRaw };
    case "enrolled":
      return { color: "success", label: statusRaw };
    case "waitlist":
      return { color: "warning", label: statusRaw };
    case "inprogress":
      return { color: "info", label: statusRaw };
    case "new":
      return { color: "primary", label: statusRaw };
    default:
      return { color: "primary", label: statusRaw || "-" };
  }
};

export default function ApplicationStatusList({ applicationData, afterLottery, schoolLotteryDate, currentlyProcessing }) {
  const { t } = useTranslation();
  const schools = applicationData?.applicationSchools || [];
  const hasInProgress = schools.some(s => String(s.status).toLowerCase() === "inprogress");
  // const isEnrolled = schools.some(s => String(s.status).toLowerCase() === "enrolled");

  const columns = afterLottery
    ? [
      { id: "school", label: t("School"), align: "left", width: "30%" },
      { id: "status", label: t("Application Status"), align: "left", width: "15%" },
      { id: "placement", label: t("Lottery Placement"), align: "left", width: "15%" },
      { id: "processing", label: t("Currently Processing"), align: "left", width: "20%" },
      ...(hasInProgress ? [{ id: "action", label: t("Action"), align: "left", width: "20%" }] : []),
    ]
    : [
      { id: "school", label: t("School"), align: "left", width: "40%" },
      { id: "status", label: t("Status"), align: "left", width: "30%" },
      { id: "date", label: t("Lottery Date"), align: "left", width: "20%" },
      ...(hasInProgress ? [{ id: "action", label: t("Action"), align: "left", width: "10%" }] : []),
    ];

  const getRowData = (item) => {
    const statusProps = getStatusProps(item.status);
    const baseData = {
      id: item.id,
      schoolName: item.school?.name || "-",
      statusProps,
      notes: item.notes,
      enrollmentPackage: item.enrollmentPackage,
    };

    if (!afterLottery) {
      return {
        ...baseData,
        col3: dayjs(schoolLotteryDate).format("MMMM D, YYYY") || "-",
      };
    }

    const lotteryStatus = item?.lotteryStatus;
    const lotteryRank = item?.lotteryRank;
    const hasLotteryRank = typeof lotteryRank === "number";

    let processingObj = null;
    if (Array.isArray(currentlyProcessing)) {
      processingObj = currentlyProcessing.find(
        (cp) =>
          cp.schoolId === item.school?.id ||
          cp.schoolId === item.schoolId
      );
    }

    let processingLabel = null;
    let processingSubtext = null;
    if (processingObj && hasLotteryRank) {
      const cpVal = processingObj.currentlyProcessing - 1;
      if (cpVal == null || cpVal === 1 || cpVal === 0) {
        processingLabel = t("Actual List");
        processingSubtext = `${Math.max(lotteryRank, 0)} ${t("applicants ahead")}`;
      } else if (cpVal > 1) {
        processingLabel = `${t("W")}${cpVal}`;
        processingSubtext = `${Math.max(lotteryRank - (cpVal), 0)} ${t("applicants ahead")}`;
      }
    } else if (hasLotteryRank) {
      processingLabel = "-";
      processingSubtext = `${Math.max(lotteryRank, 0)} ${t("applicants ahead")}`;
    }

    return {
      ...baseData,
      col3: hasLotteryRank ? `${lotteryStatus}${lotteryRank}` : null,
      processingLabel,
      processingSubtext,
    };
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t("Application Status")}
        </Typography>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 400 }} aria-label="application status table">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    // textTransform: "uppercase",
                    fontSize: "0.75rem",
                    width: col.width,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {schools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("No applications found.")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              schools.map((item) => {
                const row = getRowData(item);
                const hasNotes = Boolean(row.notes && String(row.notes).trim().length > 0);
                const isInProgress = String(item.status).toLowerCase() === "inprogress";
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.schoolName}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "inline-flex", gap: 0.5 }}>
                        <Chip
                          label={capitalize(row.statusProps.label)}
                          color={row.statusProps.color}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        {hasNotes && (
                          <Tooltip title={row.notes} placement="top" arrow>
                            <IconButton size="small" color="info" sx={{ p: 0.5 }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      {afterLottery && row.col3 ? (
                        <Chip label={row.col3} variant="outlined" size="small" color="primary" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {row.col3}
                        </Typography>
                      )}
                    </TableCell>

                    {afterLottery && (
                      <TableCell>
                        {row.processingLabel || row.processingSubtext ? (
                          <Stack>
                            {row.processingLabel && (
                              <Chip label={row.processingLabel} variant="contained" size="small" sx={{ fontWeight: 500, width: "fit-content" }} />
                            )}
                            {row.processingSubtext && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {row.processingSubtext}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    )}

                    {hasInProgress && (
                      <TableCell>
                        {isInProgress ? (
                          row.enrollmentPackage && !row.enrollmentPackage.finalized ? (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              component="a"
                              href={`/admissions/enrollment/${btoa(encodeURIComponent(row.id))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("Enroll Now")}
                            </Button>
                          ) : row.enrollmentPackage && row.enrollmentPackage.finalized ? (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              component="a"
                              href={`/admissions/enrollment/${btoa(encodeURIComponent(row.id))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("Upload Files")}
                            </Button>
                          ) : null
                        ) : null}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}